import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, ShieldCheck } from "lucide-react";
import { InviteDialog, InvitationStatus, ResendInviteButton } from "@/components/InviteDialog";
import { useAuth } from "@/context/AuthContext";
import { DeleteUserButton } from "@/components/DeleteUserButton";
import { DeactivateUserButton } from "@/components/DeactivateUserButton";

const ROLES = [
  { value: "volunteer", label: "Vrijwilliger" },
  { value: "planner", label: "Planner" },
  { value: "admin", label: "Beheerder" },
];
const emptyUser = { name: "", email: "", password: "", role: "volunteer" };
export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyUser);
  const [busy, setBusy] = useState(false);
  const [rules, setRules] = useState([]);
  const [rform, setRform] = useState({ user_a: "", user_b: "", type: "never" });

  const load = async () => {
    try {
      const [uu, rr] = await Promise.all([api.get("/users"), api.get("/users/pairing-rules")]);
      setUsers(uu.data); setRules(rr.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Gebruiker aangemaakt");
      setOpen(false); setForm(emptyUser); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changeRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Rol bijgewerkt"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleExperienced = async (u) => {
    try { await api.put(`/users/${u.id}/experienced`, { experienced: !u.experienced }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addRule = async () => {
    if (!rform.user_a || !rform.user_b) return;
    try { await api.post("/users/pairing-rules", rform); toast.success("Koppel-regel toegevoegd"); setRform({ user_a: "", user_b: "", type: "never" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteRule = async (id) => {
    try { await api.delete(`/users/pairing-rules/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const members = users.filter((u) => u.role !== "admin" || true);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight flex items-center gap-2"><ShieldCheck className="w-7 h-7 text-red-600" /> Gebruikers & rollen</h1>
          <p className="text-muted-foreground mt-1">Beheer accounts, rollen en toegang.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <InviteDialog onUpdated={load} />
          <Button onClick={() => { setForm(emptyUser); setOpen(true); }} data-testid="new-user-btn"><Plus className="w-4 h-4 mr-1.5" /> Nieuwe gebruiker</Button>
        </div>
      </div>

      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Naam</TableHead><TableHead>E-mail</TableHead><TableHead className="w-44">Rol</TableHead><TableHead>Ervaren</TableHead><TableHead>Uitnodiging</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Acties</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                <TableCell>
                  <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)} disabled={u.id === user.id}>
                    <SelectTrigger data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant={u.experienced ? "default" : "outline"} onClick={() => toggleExperienced(u)} data-testid={`experienced-toggle-${u.id}`}>{u.experienced ? "Ervaren" : "—"}</Button>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-2">
                    <InvitationStatus employee={u} />
                    <ResendInviteButton employee={u} onUpdated={load} />
                  </div>
                </TableCell>
                <TableCell>
                  <span
                    data-testid={`user-status-${u.id}`}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${u.active ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200" : "bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300"}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${u.active ? "bg-red-500" : "bg-slate-400"}`} />
                    {u.active ? "Actief" : "Inactief"}
                  </span>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    {u.id !== user.id ? (
                      <>
                        <DeactivateUserButton employee={u} onUpdated={load} />
                        <DeleteUserButton employee={u} onDeleted={load} />
                      </>
                    ) : <span className="text-xs text-muted-foreground" data-testid="delete-user-self-protected">Eigen account</span>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Card className="p-5 space-y-4" data-testid="pairing-rules-card">
        <div>
          <h2 className="font-heading text-lg font-bold">Koppel-regels</h2>
          <p className="text-sm text-muted-foreground">Markeer twee personen die nooit of juist altijd samen ingepland moeten worden. De planner krijgt een waarschuwing bij het plannen.</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <Select value={rform.user_a} onValueChange={(v) => setRform((f) => ({ ...f, user_a: v }))}><SelectTrigger className="w-48" data-testid="pair-user-a"><SelectValue placeholder="Persoon 1" /></SelectTrigger><SelectContent>{members.filter((u) => u.role !== "admin").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
          <Select value={rform.type} onValueChange={(v) => setRform((f) => ({ ...f, type: v }))}><SelectTrigger className="w-40" data-testid="pair-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="never">nooit samen met</SelectItem><SelectItem value="always">altijd samen met</SelectItem></SelectContent></Select>
          <Select value={rform.user_b} onValueChange={(v) => setRform((f) => ({ ...f, user_b: v }))}><SelectTrigger className="w-48" data-testid="pair-user-b"><SelectValue placeholder="Persoon 2" /></SelectTrigger><SelectContent>{members.filter((u) => u.role !== "admin").map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
          <Button onClick={addRule} data-testid="pair-add">Toevoegen</Button>
        </div>
        <div className="space-y-1.5">
          {rules.length === 0 && <p className="text-sm text-muted-foreground">Nog geen koppel-regels.</p>}
          {rules.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg border p-2.5 text-sm" data-testid={`pair-rule-${r.id}`}>
              <span><strong>{r.user_a_name}</strong> {r.type === "never" ? "nooit samen met" : "altijd samen met"} <strong>{r.user_b_name}</strong></span>
              <Button size="sm" variant="ghost" className="hover:text-rose-600" onClick={() => deleteRule(r.id)} data-testid={`pair-delete-${r.id}`}>Verwijderen</Button>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>Nieuwe gebruiker</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Naam</Label><Input value={form.name} onChange={set("name")} data-testid="user-name" /></div>
            <div className="space-y-1.5"><Label>E-mail</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="user-email" /></div>
            <div className="space-y-1.5"><Label>Wachtwoord (min. 8)</Label><Input type="password" value={form.password} onChange={set("password")} data-testid="user-password" /></div>
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={create} disabled={busy || !form.name || !form.email || form.password.length < 8} data-testid="user-save">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
